import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for non-local environments. Playwright builds alias this
 * module to a no-op so product bootstrap stays free of test switches.
 */
export function initSentry(options: {
    environment: string | undefined;
    instanceName: string | undefined;
    // biome-ignore lint/suspicious/noExplicitAny: TanStack router instance typing
    router: any;
}): void {
    const { environment, instanceName, router } = options;
    if (!environment || environment === "local") {
        return;
    }

    const isCopilotModeEarly =
        window.location.hash.includes("/copilot") ||
        window.location.search.includes("mode=copilot");
    const isWidgetModeEarly =
        window.location.hash.includes("/widget/") || window.location.search.includes("mode=widget");

    Sentry.init({
        dsn: "https://8f33466b4db54a81a4676c90d9d3da48@crash.siemens-energy.com/67",
        beforeSend(event, hint) {
            const error = hint?.originalException;
            if (error instanceof Error) {
                if (
                    error.name === "BrowserAuthError" ||
                    error.message?.includes("BrowserAuthError")
                ) {
                    return null;
                }
            }

            if (event.exception && event.event_id) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }

            return event;
        },

        integrations: [
            Sentry.tanstackRouterBrowserTracingIntegration(router),
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.captureConsoleIntegration({ levels: ["error"] }),
            ...(import.meta.env.VITE_FEATURES?.includes("SENTRY_USER_FEEDBACK")
                ? [
                      Sentry.feedbackIntegration({
                          autoInject: !isCopilotModeEarly && !isWidgetModeEarly,
                          showName: true,
                          showEmail: true,
                          enableScreenshot: true,
                          formTitle: "Report a Bug",
                          submitButtonLabel: "Submit",
                          successMessageText: "Thank you! Your bug report has been sent.",
                          messagePlaceholder:
                              "Please describe what's happening. \n\nIf you're experiencing an issue, please include steps to reproduce and any relevant details. Note: If your network is blocking connections to our error reporting service, your report may not be sent. Please check your network settings or contact your IT department.",
                          buttonLabel: "Report Bug",
                          triggerLabel: "Report Bug",
                          themeLight: {
                              submitBackground: "#4D217A",
                              submitBackgroundHover: "#5D318A",
                          },
                          themeDark: {
                              submitBackground: "#4D217A",
                              submitBackgroundHover: "#5D318A",
                          },
                      }),
                  ]
                : []),
        ],

        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: environment.toUpperCase(),
        initialScope: {
            tags: { _instance_name: instanceName },
        },
        ignoreErrors: [
            "ERR_BLOCKED_BY_CLIENT",
            "Failed to fetch",
            "NetworkError",
            "Non-Error promise rejection captured",
            /^BrowserAuthError/,
            "BrowserAuthError",
            "interaction_in_progress",
            /useMsalAuthentication.*Interaction required/,
            "cancelled",
        ],
    });
}
